import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { AcceptInviteShell } from "./accept-invite-shell";

const schema = z.object({
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code."),
});
type Values = z.infer<typeof schema>;

interface OtpStepProps {
  maskedPhone: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
}

export function OtpStep({ maskedPhone, onVerify, onResend }: OtpStepProps) {
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { code: "" },
  });

  const handleVerify = async ({ code }: Values) => {
    try {
      await onVerify(code);
    } catch (e) {
      form.setError("code", {
        message: e instanceof Error ? e.message : "That code didn't match.",
      });
    }
  };

  const handleResend = async () => {
    form.reset();
    try {
      await onResend();
    } catch (e) {
      form.setError("code", {
        message: e instanceof Error ? e.message : "Couldn't resend the code.",
      });
    }
  };

  return (
    <AcceptInviteShell
      title="First, enter your code"
      subtitle={`We texted a 6-digit code to ${maskedPhone}. Enter it to keep going.`}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleVerify)} className="mt-7">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">Verification code</FormLabel>
                <FormControl>
                  <InputOTP
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern={REGEXP_ONLY_DIGITS}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    <InputOTPGroup>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <InputOTPSlot
                          key={i}
                          index={i}
                          className="h-12 w-12 text-base"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="mt-8 flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={handleResend}
            >
              Resend code
            </Button>
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="rounded-full bg-black text-white hover:bg-black/90"
            >
              {form.formState.isSubmitting ? "Verifying…" : "Verify"}
            </Button>
          </div>
        </form>
      </Form>
    </AcceptInviteShell>
  );
}
